"use client";

import * as React from "react";
import { createPortal } from "react-dom";
import type { CSSProperties, HTMLAttributes } from "react";
import { X } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { Z_INDEX } from "./z-index";
import { DropdownAuroraGlow } from "./composites/dropdown-aurora-glow";
import { LiquidPill } from "./liquid-pill";
import { V2_MOTION } from "./motion";

const MODAL_OPEN_MS = 460;
const MODAL_HEIGHT_MORPH_MS = 860;
const MODAL_ENTER_MS = 700;
const MODAL_EXIT_MS = 340;
const MODAL_MAX_HEIGHT_PX = 900;
const MODAL_MAX_HEIGHT_VH = 0.9;
const MODAL_COMPACT_LOADING_HEIGHT_PX = 168;
const MODAL_COMPACT_LOADING_MAX_WIDTH_PX = 296;
/** Ignore sub-pixel / scrollbar feedback when morphing height. */
const MODAL_HEIGHT_EPSILON_PX = 4;

function ModalCompactLoadingMark({ label }: { label: string }) {
  return (
    <div className="flex h-full min-h-0 w-full flex-col items-center justify-center gap-5 px-7 text-center">
      <style>{`
        @keyframes modal-loader-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
      <div
        className="relative h-14 w-14"
        role="status"
        aria-label={label}
      >
        <span className="absolute inset-0 rounded-full border border-slate-200/60 dark:border-zinc-700/60" />
        <span
          className="absolute inset-0 rounded-full"
          style={{
            background: [
              "conic-gradient(from 0deg,",
              "transparent 0deg,",
              "transparent 120deg,",
              "#38bdf8 175deg,",
              "#6366f1 230deg,",
              "#a855f7 280deg,",
              "#ec4899 320deg,",
              "#f472b6 340deg,",
              "#ffffff 352deg,",
              "transparent 360deg",
              ")",
            ].join(" "),
            WebkitMask:
              "radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 2.25px))",
            mask:
              "radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 2.25px))",
            filter: "drop-shadow(0 0 6px rgba(168,85,247,0.35))",
            animation: "modal-loader-spin 0.85s linear infinite",
          }}
        />
      </div>
      <div className="space-y-1">
        <p className="text-[13px] font-semibold leading-snug text-slate-900 dark:text-zinc-50">
          {label}
        </p>
        <p className="text-[9px] leading-snug text-slate-500 dark:text-zinc-400">
          Un momento, por favor
        </p>
      </div>
    </div>
  );
}

function getModalMaxHeightPx(panel?: HTMLElement | null) {
  if (typeof window === "undefined") return MODAL_MAX_HEIGHT_PX;
  const viewportCap = Math.min(
    window.innerHeight * MODAL_MAX_HEIGHT_VH,
    MODAL_MAX_HEIGHT_PX
  );
  if (!panel) return viewportCap;

  const computedMax = window.getComputedStyle(panel).maxHeight;
  if (!computedMax || computedMax === "none") return viewportCap;
  const parsed = Number.parseFloat(computedMax);
  if (!Number.isFinite(parsed) || parsed <= 0) return viewportCap;
  return Math.min(parsed, viewportCap);
}

function readVerticalPadding(element: HTMLElement) {
  const styles = window.getComputedStyle(element);
  return (
    (Number.parseFloat(styles.paddingTop) || 0) +
    (Number.parseFloat(styles.paddingBottom) || 0)
  );
}

/**
 * Intrinsic panel height from real content, not from a height-locked /
 * flex-constrained wrapper (that feedback loop slowly shrinks the modal).
 *
 * Content may wrap header/body/footer in an intermediate shell (bubbleReveal).
 * Measure that shell's sections — not only the body probe — or the panel
 * locks short and clips mid-content.
 */
function measureIntrinsicModalHeight(args: {
  content: HTMLElement;
  body: HTMLElement | null;
  bodyContent: HTMLElement | null;
}) {
  const { content, body, bodyContent } = args;
  let naturalHeight = 0;

  const contentChildren = Array.from(content.children) as HTMLElement[];
  let sectionsRoot: HTMLElement = content;
  if (body) {
    const wrapper = contentChildren.find((el) => el.contains(body));
    if (wrapper) sectionsRoot = wrapper;
  }

  Array.from(sectionsRoot.children).forEach((child) => {
    const el = child as HTMLElement;
    if (body && (el === body || el.contains(body))) {
      const probe = bodyContent ?? body;
      naturalHeight += Math.ceil(probe.scrollHeight) + readVerticalPadding(body);
      return;
    }
    // offsetHeight ignores ancestor transforms (enter pop scale).
    naturalHeight += Math.ceil(el.offsetHeight);
  });

  return Math.max(0, Math.ceil(naturalHeight));
}

/** Body fade: aurora visible arriba, fondo sólido en todo el alto (evita franja gris en loading). */
export const MODAL_BODY_CLASS_NAME =
  "bg-gradient-to-b from-transparent via-white/95 via-[10%] to-white to-100% dark:via-zinc-950/95 dark:to-zinc-950";

const modalSizeVariants = cva(
  [
    "relative mx-auto flex w-full flex-col overflow-hidden rounded-[24px]",
    "border border-slate-200/70 bg-white/95 backdrop-blur-xl",
    "shadow-[0_32px_100px_-18px_rgba(15,23,42,0.35),0_18px_48px_-18px_rgba(79,70,229,0.18),inset_0_1px_0_rgba(255,255,255,0.72)]",
    "dark:border-zinc-800/80 dark:bg-zinc-950/95 dark:shadow-[0_32px_100px_-18px_rgba(0,0,0,0.58),0_18px_48px_-18px_rgba(99,102,241,0.2),inset_0_1px_0_rgba(255,255,255,0.05)]",
  ].join(" "),
  {
    variants: {
      size: {
        xs: "max-w-sm",
        sm: "max-w-md",
        md: "max-w-2xl",
        lg: "max-w-4xl",
        xl: "max-w-5xl",
        full: "max-w-[min(100vw-1.5rem,88rem)]",
      },
    },
    defaultVariants: {
      size: "md",
    },
  }
);

const MODAL_SIZE_MAX_WIDTH_PX: Record<
  NonNullable<VariantProps<typeof modalSizeVariants>["size"]>,
  number
> = {
  xs: 384,
  sm: 448,
  md: 672,
  lg: 896,
  xl: 1024,
  full: 1408,
};

function getModalTargetWidthPx(
  size: VariantProps<typeof modalSizeVariants>["size"]
) {
  if (typeof window === "undefined") {
    return MODAL_SIZE_MAX_WIDTH_PX[size || "md"];
  }
  const viewportBudget = Math.max(280, window.innerWidth - 40);
  return Math.min(MODAL_SIZE_MAX_WIDTH_PX[size || "md"], viewportBudget);
}

export interface ModalProps extends VariantProps<typeof modalSizeVariants> {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  eyebrow?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  header?: React.ReactNode;
  className?: string;
  panelClassName?: string;
  panelStyle?: CSSProperties;
  headerClassName?: string;
  bodyClassName?: string;
  footerClassName?: string;
  backdrop?: boolean | "static";
  keyboard?: boolean;
  showCloseButton?: boolean;
  closeButtonLabel?: string;
  /** Render only positioning/sizing shell, without visual panel chrome, border, glow or shadow. */
  unstyledPanel?: boolean;
  /** Fade at the top of the scrollable body while the user scrolls long content. */
  bodyScrollFade?: boolean;
  /** Animate panel height when content grows/shrinks (liquid bounce). */
  animateHeight?: boolean;
  /** Give the complete panel a liquid breathing state and block body actions. */
  loading?: boolean;
  /**
   * Deferred open: show a compact panel with spinner + label while `loading`,
   * then swap to full content when ready (no enter-animation replay on load end).
   */
  bubbleReveal?: boolean;
  /** Label shown in the compact loading state when `bubbleReveal` is enabled. */
  loadingLabel?: string;
}

export function Modal({
  open,
  onClose,
  title,
  description,
  eyebrow,
  children,
  footer,
  header,
  size = "md",
  className,
  panelClassName,
  panelStyle,
  headerClassName,
  bodyClassName,
  footerClassName,
  backdrop = true,
  keyboard = true,
  showCloseButton = true,
  closeButtonLabel = "Close modal",
  unstyledPanel = false,
  bodyScrollFade = false,
  animateHeight = true,
  loading = false,
  bubbleReveal = false,
  loadingLabel = "Cargando contenido",
}: ModalProps) {
  const [mounted, setMounted] = React.useState(false);
  const [shouldRender, setShouldRender] = React.useState(open);
  // Always start hidden so the open effect can play the pop enter animation.
  const [isVisible, setIsVisible] = React.useState(false);
  const [renderLoadingVisual, setRenderLoadingVisual] = React.useState(loading);
  const [loadingVisualActive, setLoadingVisualActive] = React.useState(loading);
  const [bodyScrollFadeOpacity, setBodyScrollFadeOpacity] = React.useState(0);
  const [contentHeight, setContentHeight] = React.useState<number | null>(null);
  const [contentNaturalHeight, setContentNaturalHeight] = React.useState<number | null>(
    null
  );
  const [heightMorphReady, setHeightMorphReady] = React.useState(false);
  /** Inline max-width lock so compact → size can transition with height. */
  const [panelMaxWidth, setPanelMaxWidth] = React.useState<number | null>(null);
  const [enterActive, setEnterActive] = React.useState(false);
  const [enterNonce, setEnterNonce] = React.useState(0);
  const bodyRef = React.useRef<HTMLDivElement>(null);
  const bodyContentRef = React.useRef<HTMLDivElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const contentHeightRef = React.useRef<number | null>(null);
  const naturalHeightRef = React.useRef<number | null>(null);
  const pendingHeightFrameRef = React.useRef<number | null>(null);
  const titleId = React.useId();
  const descriptionId = React.useId();
  const hasHeaderContent = Boolean(header || eyebrow || title || description);
  const showHeader = hasHeaderContent;
  const heightMorphEnabled = animateHeight && !unstyledPanel;
  const showCompactLoader = bubbleReveal && loading;
  const prevOpenRef = React.useRef(false);
  const wasBubbleLoadingRef = React.useRef(false);
  const revealMorphPendingRef = React.useRef(false);
  const revealMorphFrameRef = React.useRef<number | null>(null);
  const revealMorphGenRef = React.useRef(0);
  const revealMorphLockUntilRef = React.useRef(0);
  const revealMorphAnimRef = React.useRef<Animation | null>(null);
  const enterSettleTimerRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  React.useEffect(() => {
    if (!mounted) return;

    if (open) {
      setShouldRender(true);
      const justOpened = !prevOpenRef.current;
      prevOpenRef.current = true;

      // loading true→false must NOT replay the enter animation.
      if (!justOpened) return;

      setEnterActive(false);
      setIsVisible(false);

      // Double rAF: mount shell hidden, then flip enter class so pop always starts at 0%.
      let outer = 0;
      let inner = 0;
      outer = window.requestAnimationFrame(() => {
        inner = window.requestAnimationFrame(() => {
          setEnterNonce((n) => n + 1);
          setIsVisible(true);
          setEnterActive(true);
          if (enterSettleTimerRef.current) {
            window.clearTimeout(enterSettleTimerRef.current);
          }
          enterSettleTimerRef.current = window.setTimeout(() => {
            setEnterActive(false);
            enterSettleTimerRef.current = null;
          }, MODAL_ENTER_MS);
        });
      });

      return () => {
        window.cancelAnimationFrame(outer);
        window.cancelAnimationFrame(inner);
        if (enterSettleTimerRef.current) {
          window.clearTimeout(enterSettleTimerRef.current);
          enterSettleTimerRef.current = null;
        }
      };
    }

    prevOpenRef.current = false;
    setEnterActive(false);
    setIsVisible(false);
    const closeId = window.setTimeout(() => setShouldRender(false), MODAL_EXIT_MS + 40);
    return () => window.clearTimeout(closeId);
  }, [mounted, open]);

  React.useEffect(() => {
    if (bubbleReveal) {
      setRenderLoadingVisual(false);
      setLoadingVisualActive(false);
      return;
    }

    if (loading) {
      setRenderLoadingVisual(true);
      const revealId = window.requestAnimationFrame(() => {
        setLoadingVisualActive(true);
      });
      return () => window.cancelAnimationFrame(revealId);
    }

    setLoadingVisualActive(false);
    const removeId = window.setTimeout(() => {
      setRenderLoadingVisual(false);
    }, 480);
    return () => window.clearTimeout(removeId);
  }, [bubbleReveal, loading]);

  const measureContentHeight = React.useCallback(() => {
    const panel = panelRef.current;
    const content = contentRef.current;
    if (!panel || !content) return;

    // Compact loader owns its fixed size; reveal morph waits one painted frame.
    if (bubbleReveal && loading) return;
    if (revealMorphPendingRef.current) return;
    if (Date.now() < revealMorphLockUntilRef.current) return;

    if (pendingHeightFrameRef.current) {
      window.cancelAnimationFrame(pendingHeightFrameRef.current);
      pendingHeightFrameRef.current = null;
    }

    const naturalHeight = measureIntrinsicModalHeight({
      content,
      body: bodyRef.current,
      bodyContent: bodyContentRef.current,
    });
    const maxHeight = getModalMaxHeightPx(panel);
    const cappedHeight = Math.min(naturalHeight, maxHeight);
    const previousHeight = contentHeightRef.current;
    const previousNatural = naturalHeightRef.current;

    naturalHeightRef.current = naturalHeight;
    setContentNaturalHeight((prev) =>
      prev === naturalHeight ? prev : naturalHeight
    );

    // No real content change — ignore constrained-layout noise.
    if (
      previousNatural !== null &&
      Math.abs(previousNatural - naturalHeight) < MODAL_HEIGHT_EPSILON_PX &&
      previousHeight !== null &&
      Math.abs(previousHeight - cappedHeight) < MODAL_HEIGHT_EPSILON_PX
    ) {
      return;
    }

    if (previousHeight !== null && Math.abs(previousHeight - cappedHeight) < MODAL_HEIGHT_EPSILON_PX) {
      return;
    }

    contentHeightRef.current = cappedHeight;
    setContentHeight(cappedHeight);
  }, [bubbleReveal, loading]);

  React.useLayoutEffect(() => {
    if (!isVisible || !heightMorphEnabled) {
      setHeightMorphReady(false);
      setContentHeight(null);
      contentHeightRef.current = null;
      naturalHeightRef.current = null;
      setContentNaturalHeight(null);
      setPanelMaxWidth(null);
      return;
    }

    if (bubbleReveal && loading) {
      // Seed compact size so height + width morph can grow into content.
      wasBubbleLoadingRef.current = true;
      revealMorphPendingRef.current = false;
      setHeightMorphReady(true);
      contentHeightRef.current = MODAL_COMPACT_LOADING_HEIGHT_PX;
      setContentHeight(MODAL_COMPACT_LOADING_HEIGHT_PX);
      setContentNaturalHeight(MODAL_COMPACT_LOADING_HEIGHT_PX);
      setPanelMaxWidth(MODAL_COMPACT_LOADING_MAX_WIDTH_PX);
      return;
    }

    if (wasBubbleLoadingRef.current) {
      // Keep wasBubbleLoading true until morph commits — Strict Mode cleanup
      // cancels rAFs; remount must be able to retry the same reveal.
      const morphGen = ++revealMorphGenRef.current;
      revealMorphPendingRef.current = true;
      contentHeightRef.current = MODAL_COMPACT_LOADING_HEIGHT_PX;
      setContentHeight(MODAL_COMPACT_LOADING_HEIGHT_PX);
      setPanelMaxWidth(MODAL_COMPACT_LOADING_MAX_WIDTH_PX);
      setHeightMorphReady(true);

      if (revealMorphFrameRef.current) {
        window.cancelAnimationFrame(revealMorphFrameRef.current);
      }

      const morphEase = V2_MOTION.ease.liquidBounce;
      const morphTransition = [
        `height ${MODAL_HEIGHT_MORPH_MS}ms ${morphEase}`,
        `max-width ${MODAL_HEIGHT_MORPH_MS}ms ${morphEase}`,
      ].join(", ");

      revealMorphFrameRef.current = window.requestAnimationFrame(() => {
        revealMorphFrameRef.current = window.requestAnimationFrame(() => {
          revealMorphFrameRef.current = null;
          if (morphGen !== revealMorphGenRef.current) return;

          const panel = panelRef.current;
          const content = contentRef.current;
          const fromHeight = MODAL_COMPACT_LOADING_HEIGHT_PX;
          const fromWidth = MODAL_COMPACT_LOADING_MAX_WIDTH_PX;
          const targetWidth = getModalTargetWidthPx(size);
          let targetHeight = fromHeight;
          let naturalHeight = fromHeight;

          if (panel && content) {
            // Measure at final width with height:auto — still before paint, so
            // the compact box is restored before the browser draws a frame.
            panel.style.transition = "none";
            panel.style.maxWidth = `${targetWidth}px`;
            panel.style.height = "auto";
            void panel.offsetHeight;

            naturalHeight = measureIntrinsicModalHeight({
              content,
              body: bodyRef.current,
              bodyContent: bodyContentRef.current,
            });
            // Prefer the larger of intrinsic sections vs panel box (header+body+footer).
            const panelBoxHeight = Math.ceil(panel.offsetHeight);
            naturalHeight = Math.max(naturalHeight, panelBoxHeight);
            targetHeight = Math.min(
              Math.max(naturalHeight, fromHeight),
              getModalMaxHeightPx(panel)
            );

            // FLIP from compact → target on both axes in the same turn.
            panel.style.transition = "none";
            panel.style.height = `${fromHeight}px`;
            panel.style.maxWidth = `${fromWidth}px`;
            void panel.offsetHeight;

            naturalHeightRef.current = naturalHeight;
            contentHeightRef.current = targetHeight;
            revealMorphLockUntilRef.current =
              Date.now() + MODAL_HEIGHT_MORPH_MS + 80;
            wasBubbleLoadingRef.current = false;
            revealMorphPendingRef.current = false;

            setContentNaturalHeight(naturalHeight);
            setContentHeight(targetHeight);
            setPanelMaxWidth(targetWidth);

            // WAAPI: height + max-width in one animation (CSS transition on
            // height is easy to lose when React re-commits styles).
            if (typeof panel.animate === "function") {
              revealMorphAnimRef.current?.cancel();
              const anim = panel.animate(
                [
                  {
                    height: `${fromHeight}px`,
                    maxWidth: `${fromWidth}px`,
                  },
                  {
                    height: `${targetHeight}px`,
                    maxWidth: `${targetWidth}px`,
                  },
                ],
                {
                  duration: MODAL_HEIGHT_MORPH_MS,
                  easing: morphEase,
                  fill: "forwards",
                }
              );
              revealMorphAnimRef.current = anim;
              void anim.finished
                .then(() => {
                  if (revealMorphAnimRef.current === anim) {
                    anim.cancel();
                    revealMorphAnimRef.current = null;
                  }
                })
                .catch(() => {
                  /* cancelled */
                });
            } else {
              panel.style.transition = morphTransition;
              panel.style.height = `${targetHeight}px`;
              panel.style.maxWidth = `${targetWidth}px`;
            }
            return;
          }

          wasBubbleLoadingRef.current = false;
          revealMorphPendingRef.current = false;
          contentHeightRef.current = targetHeight;
          setContentHeight(targetHeight);
          setPanelMaxWidth(targetWidth);
        });
      });
      return () => {
        // Invalidate in-flight morph; leave wasBubbleLoading armed for retry.
        revealMorphGenRef.current += 1;
        revealMorphAnimRef.current?.cancel();
        revealMorphAnimRef.current = null;
        if (revealMorphFrameRef.current) {
          window.cancelAnimationFrame(revealMorphFrameRef.current);
          revealMorphFrameRef.current = null;
        }
      };
    }

    if (loading) {
      setHeightMorphReady(true);
      return;
    }

    // Post-loading: mantener altura bloqueada para animar el crecimiento.
    if (contentHeightRef.current !== null) {
      setHeightMorphReady(true);
      return;
    }

    setHeightMorphReady(false);
    const readyId = window.setTimeout(
      () => setHeightMorphReady(true),
      MODAL_OPEN_MS
    );
    return () => window.clearTimeout(readyId);
  }, [
    bubbleReveal,
    heightMorphEnabled,
    isVisible,
    loading,
    measureContentHeight,
    size,
  ]);

  React.useLayoutEffect(() => {
    if (!heightMorphReady || !heightMorphEnabled) return;
    measureContentHeight();
  }, [heightMorphEnabled, heightMorphReady, measureContentHeight]);

  React.useLayoutEffect(() => {
    if (!shouldRender || !heightMorphEnabled || !isVisible) return;
    measureContentHeight();
  }, [
    heightMorphEnabled,
    bodyScrollFade,
    children,
    footer,
    isVisible,
    loading,
    measureContentHeight,
    shouldRender,
    showHeader,
  ]);

  React.useLayoutEffect(() => {
    if (!heightMorphEnabled || !isVisible) return;
    if (!loading) {
      // Tras el swap loading → contenido, un segundo frame asegura layout final.
      const frameId = window.requestAnimationFrame(() => {
        measureContentHeight();
      });
      return () => window.cancelAnimationFrame(frameId);
    }
    measureContentHeight();
  }, [heightMorphEnabled, isVisible, loading, measureContentHeight]);

  React.useEffect(() => {
    if (!shouldRender || !heightMorphEnabled || !contentRef.current) return;

    measureContentHeight();

    if (typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(() => {
      measureContentHeight();
    });

    // Observe the intrinsic content probe (grows/shrinks with children),
    // not the flex-constrained shell that mirrors the locked panel height.
    const probe = bodyContentRef.current ?? bodyRef.current ?? contentRef.current;
    observer.observe(probe);
    if (contentRef.current && contentRef.current !== probe) {
      observer.observe(contentRef.current);
    }

    const handleWindowResize = () => measureContentHeight();
    window.addEventListener("resize", handleWindowResize);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", handleWindowResize);
    };
  }, [heightMorphEnabled, measureContentHeight, shouldRender]);

  React.useEffect(
    () => () => {
      if (pendingHeightFrameRef.current) {
        window.cancelAnimationFrame(pendingHeightFrameRef.current);
      }
      if (revealMorphFrameRef.current) {
        window.cancelAnimationFrame(revealMorphFrameRef.current);
      }
      if (revealMorphAnimRef.current) {
        revealMorphAnimRef.current.cancel();
        revealMorphAnimRef.current = null;
      }
      if (enterSettleTimerRef.current) {
        window.clearTimeout(enterSettleTimerRef.current);
      }
    },
    []
  );

  React.useEffect(() => {
    if (!mounted || !shouldRender) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mounted, shouldRender]);

  React.useEffect(() => {
    if (!open || !keyboard) {
      return;
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [keyboard, onClose, open]);

  const handleBackdropClick = React.useCallback(() => {
    if (backdrop === true) {
      onClose();
    }
  }, [backdrop, onClose]);

  const handleBodyScroll = React.useCallback(() => {
    if (!bodyScrollFade) return;

    const scrollTop = bodyRef.current?.scrollTop ?? 0;
    const nextOpacity = Math.min(scrollTop / 28, 1);

    setBodyScrollFadeOpacity((prev) =>
      Math.abs(prev - nextOpacity) < 0.02 ? prev : nextOpacity
    );
  }, [bodyScrollFade]);

  React.useEffect(() => {
    if (!open || !bodyScrollFade) {
      setBodyScrollFadeOpacity(0);
      return;
    }

    bodyRef.current?.scrollTo({ top: 0 });
    setBodyScrollFadeOpacity(0);
  }, [bodyScrollFade, open]);

  const unstyledSizeClass = {
    xs: "max-w-sm",
    sm: "max-w-md",
    md: "max-w-2xl",
    lg: "max-w-4xl",
    xl: "max-w-5xl",
    full: "max-w-[min(100vw-1.5rem,88rem)]",
  }[size || "md"];

  if (!mounted || !shouldRender) {
    return null;
  }

  const shouldAnimatePanelHeight =
    heightMorphEnabled && isVisible && contentHeight !== null;
  const isPanelHeightCapped =
    contentNaturalHeight !== null &&
    contentHeight !== null &&
    contentNaturalHeight > contentHeight;
  const shouldLockPanelHeight =
    shouldAnimatePanelHeight &&
    heightMorphReady &&
    contentHeight !== null &&
    contentHeight > 0;
  const shouldAnimatePanelWidth = panelMaxWidth !== null;
  const panelTransition = [
    shouldLockPanelHeight || showCompactLoader
      ? `height ${MODAL_HEIGHT_MORPH_MS}ms ${
          bubbleReveal ? V2_MOTION.ease.liquidBounce : V2_MOTION.ease.premiumReveal
        }`
      : null,
    shouldAnimatePanelWidth || showCompactLoader
      ? `max-width ${MODAL_HEIGHT_MORPH_MS}ms ${
          bubbleReveal ? V2_MOTION.ease.liquidBounce : V2_MOTION.ease.premiumReveal
        }`
      : null,
  ]
    .filter(Boolean)
    .join(", ");
  const loadingPanelAnimation =
    !enterActive &&
    loading &&
    !bubbleReveal &&
    isVisible &&
    !unstyledPanel
      ? "modal-liquid-loading-breathe 2400ms cubic-bezier(0.45, 0, 0.55, 1) infinite"
      : !enterActive &&
          renderLoadingVisual &&
          isVisible &&
          !bubbleReveal &&
          !unstyledPanel
        ? "modal-liquid-loading-settle 480ms cubic-bezier(0.16, 1, 0.3, 1) both"
        : null;
  const panelAnimation = loadingPanelAnimation || undefined;

  return createPortal(
    <>
      <style>{`
          @keyframes modal-panel-enter {
            0% {
              opacity: 1;
              transform: scale(0.05);
            }
            70% {
              opacity: 1;
              transform: scale(1.1);
            }
            100% {
              opacity: 1;
              transform: scale(1);
            }
          }

          .modal-v2-enter-pop {
            animation: modal-panel-enter ${MODAL_ENTER_MS}ms cubic-bezier(0.12, 1.35, 0.28, 1) both;
            transform-origin: center center;
          }

          @keyframes modal-backdrop-enter {
            0% { opacity: 0; }
            100% { opacity: 1; }
          }

          @keyframes modal-liquid-loading-breathe {
            0%, 100% {
              scale: 0.987 1.01;
              translate: -1px 1px;
              rotate: -0.08deg;
              border-radius: 24px 30px 22px 28px / 28px 22px 30px 24px;
              filter: blur(0) saturate(1) brightness(1);
            }
            22% {
              scale: 1.009 0.991;
              translate: 2px -3px;
              rotate: 0.09deg;
              border-radius: 30px 22px 29px 21px / 22px 30px 23px 29px;
              filter: blur(0) saturate(1.06) brightness(1.012);
            }
            48% {
              scale: 0.993 1.013;
              translate: 1px -1px;
              rotate: -0.07deg;
              border-radius: 21px 29px 24px 32px / 30px 23px 29px 22px;
              filter: blur(0) saturate(1.11) brightness(1.024);
            }
            74% {
              scale: 1.012 0.989;
              translate: -2px 2px;
              rotate: 0.1deg;
              border-radius: 28px 24px 31px 22px / 23px 29px 21px 31px;
              filter: blur(0) saturate(1.07) brightness(1.014);
            }
          }

          @keyframes modal-liquid-loading-settle {
            0% {
              scale: 0.995 1.007;
              translate: -1px 1px;
              rotate: -0.04deg;
              border-radius: 25px 28px 23px 27px / 27px 23px 28px 24px;
              filter: saturate(1.035) brightness(1.008);
            }
            100% {
              scale: 1;
              translate: 0;
              rotate: 0deg;
              border-radius: 24px;
              filter: saturate(1) brightness(1);
            }
          }

          @media (prefers-reduced-motion: reduce) {
            .modal-v2-enter-pop {
              animation-duration: 200ms;
            }
            @keyframes modal-panel-enter {
              0% {
                opacity: 1;
                transform: scale(0.2);
              }
              100% {
                opacity: 1;
                transform: scale(1);
              }
            }
            @keyframes modal-backdrop-enter {
              0% { opacity: 0; }
              100% { opacity: 1; }
            }
          }
        `}</style>
    <div
      className={cn(
        "fixed inset-0 flex items-center justify-center p-2.5 sm:p-3.5 lg:p-5",
        className
      )}
      style={{ zIndex: Z_INDEX.MODAL }}
      aria-hidden={!isVisible}
    >
      {backdrop !== false && (
        <button
          type="button"
          aria-label="Close modal backdrop"
          className="absolute inset-0 cursor-default bg-[radial-gradient(circle_at_top,rgba(99,102,241,0.15),transparent_32%)] backdrop-blur-[6px]"
          style={{
            opacity: isVisible ? 1 : 0,
            backgroundColor: "rgba(0, 0, 0, 0.45)",
            animation:
              enterActive && isVisible
                ? `modal-backdrop-enter ${MODAL_ENTER_MS - 80}ms ${V2_MOTION.ease.premiumReveal} both`
                : undefined,
            transition: enterActive
              ? undefined
              : `opacity ${MODAL_EXIT_MS}ms ${V2_MOTION.ease.premiumReveal}`,
          }}
          onClick={handleBackdropClick}
        />
      )}

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={!title ? "Modal" : undefined}
        aria-labelledby={title && !showCompactLoader ? titleId : undefined}
        aria-describedby={description && !showCompactLoader ? descriptionId : undefined}
        aria-busy={loading || showCompactLoader || undefined}
        className={cn(
          unstyledPanel
            ? "relative mx-auto flex w-full flex-col overflow-visible bg-transparent shadow-none"
            : modalSizeVariants({ size }),
          showCompactLoader
            ? "max-w-[18.5rem] max-h-none"
            : unstyledPanel
              ? unstyledSizeClass
              : "max-h-[min(90vh,900px)]",
          // Enter pop is unconditional (including unstyledPanel / ExploreModal
          // bridge). Never gate this on chrome props — see design_language §5.5.
          enterActive && "modal-v2-enter-pop",
          loading &&
            !bubbleReveal &&
            !unstyledPanel &&
            "will-change-[transform,filter,border-radius]",
          heightMorphEnabled && heightMorphReady
            ? "will-change-[height,max-width,transform]"
            : "will-change-transform",
          !showCompactLoader && panelClassName
        )}
        style={{
          ...panelStyle,
          // While popping, leave opacity to the CSS animation.
          opacity: enterActive ? undefined : isVisible ? 1 : 0,
          height:
            showCompactLoader || shouldLockPanelHeight
              ? contentHeight ?? MODAL_COMPACT_LOADING_HEIGHT_PX
              : undefined,
          maxWidth:
            panelMaxWidth ??
            (showCompactLoader ? MODAL_COMPACT_LOADING_MAX_WIDTH_PX : undefined),
          // Same invariant as className above: enter runs for every Modal open.
          ...(enterActive
            ? {
                animation: `modal-panel-enter ${MODAL_ENTER_MS}ms cubic-bezier(0.12, 1.35, 0.28, 1) both`,
              }
            : panelAnimation
              ? { animation: panelAnimation }
              : {}),
          transition: enterActive
            ? panelTransition
            : [
                `opacity ${MODAL_EXIT_MS}ms ${V2_MOTION.ease.premiumReveal}`,
                panelTransition,
              ]
                .filter(Boolean)
                .join(", "),
          transformOrigin: "center center",
        }}
      >
        {!unstyledPanel && (
          <>
            <div className="pointer-events-none absolute inset-0 rounded-[inherit] border border-white/50 dark:border-white/5" />
            <DropdownAuroraGlow variant="modal" />
            {renderLoadingVisual && !bubbleReveal ? (
              <div
                className={cn(
                  "absolute inset-0 z-50 flex items-center justify-center rounded-[inherit]",
                  "bg-white dark:bg-zinc-950",
                  "transition-opacity duration-500",
                  loadingVisualActive
                    ? "pointer-events-auto"
                    : "pointer-events-none"
                )}
                style={{ opacity: loadingVisualActive ? 1 : 0 }}
                aria-hidden={!loadingVisualActive}
              >
                <ModalCompactLoadingMark label={loadingLabel} />
              </div>
            ) : null}
          </>
        )}

        {/* flex-1 solo actúa cuando el panel es flex (p.ej. panelClassName
            con min-h + flex flex-col): estira el body para que el fondo
            blanco cubra todo el alto del panel. */}
        <div
          ref={contentRef}
          className={cn(
            "relative z-10 flex flex-col",
            showCompactLoader ||
              bodyScrollFade ||
              (isPanelHeightCapped &&
                shouldAnimatePanelHeight &&
                heightMorphReady)
              ? "min-h-0 flex-1"
              : "shrink-0"
          )}
        >
          {showCompactLoader ? (
            <ModalCompactLoadingMark label={loadingLabel} />
          ) : null}

          {/* Always keep the same tree for children so queries are not remounted
              when leaving the compact loader. */}
          <div
            className={cn(
              "flex min-h-0 flex-1 flex-col",
              showCompactLoader &&
                "pointer-events-none absolute h-px w-px overflow-hidden opacity-0"
            )}
            aria-hidden={showCompactLoader || undefined}
          >
          {showHeader && (
            <div
              className={cn(
                "relative flex shrink-0 items-center justify-between gap-3 bg-transparent px-4 py-3 sm:px-5 sm:py-3.5",
                headerClassName
              )}
            >
              <div className="min-w-0 flex-1">
                {header ?? (
                  <>
                    {eyebrow && (
                      <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-indigo-500/90 dark:text-indigo-300/80">
                        {eyebrow}
                      </p>
                    )}
                    {title && (
                      <h2
                        id={titleId}
                        className="mt-0.5 text-[13px] font-semibold leading-snug text-slate-900 dark:text-zinc-50 sm:text-[15px]"
                      >
                        {title}
                      </h2>
                    )}
                    {description && (
                      <p
                        id={descriptionId}
                        className="mt-1 max-w-2xl text-[11px] text-slate-500 dark:text-zinc-400 sm:text-xs"
                      >
                        {description}
                      </p>
                    )}
                  </>
                )}
              </div>

              {showCloseButton && hasHeaderContent && (
                <LiquidPill
                  as="button"
                  type="button"
                  onClick={onClose}
                  aria-label={closeButtonLabel}
                  variant="rose"
                  appearance="tinted"
                  shape="circle"
                  size="sm"
                  icon={<X className="h-3.5 w-3.5" />}
                />
              )}
            </div>
          )}

          {bodyScrollFade ? (
            <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
              <div
                ref={bodyRef}
                onScroll={handleBodyScroll}
                aria-busy={loading || undefined}
                className={cn(
                  "flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain px-4 sm:px-5",
                  loading && "pointer-events-none select-none",
                  // Self-contained shells (ExploreModal, etc.) bring their own
                  // surface — don't paint Modal's white body behind them.
                  !unstyledPanel && MODAL_BODY_CLASS_NAME,
                  showHeader ? "pb-4 pt-1 sm:pb-5 sm:pt-1.5" : "py-4 sm:py-5",
                  bodyClassName
                )}
              >
                <div ref={bodyContentRef} className="flex w-full flex-col">
                  {children}
                </div>
              </div>
              <div
                aria-hidden
                className={cn(
                  "pointer-events-none absolute inset-x-0 top-0 z-20 h-10",
                  "bg-gradient-to-b from-white/95 via-white/75 to-transparent",
                  "dark:from-zinc-950/95 dark:via-zinc-950/75"
                )}
                style={{
                  opacity: bodyScrollFadeOpacity,
                  transition: "opacity 180ms ease",
                }}
              />
            </div>
          ) : (
            <div
              ref={bodyRef}
              aria-busy={loading || undefined}
              className={cn(
                "relative flex flex-col px-4 sm:px-5",
                loading && "pointer-events-none select-none",
                isPanelHeightCapped && shouldAnimatePanelHeight && heightMorphReady
                  ? "min-h-0 flex-1 overflow-y-auto"
                  : "overflow-y-auto",
                // Self-contained shells (ExploreModal, etc.) bring their own
                // surface — don't paint Modal's white body behind them.
                !unstyledPanel && MODAL_BODY_CLASS_NAME,
                showHeader ? "pb-4 pt-1 sm:pb-5 sm:pt-1.5" : "py-4 sm:py-5",
                bodyClassName
              )}
            >
              <div ref={bodyContentRef} className="flex w-full flex-col">
                {children}
              </div>
            </div>
          )}

          {footer ? (
            <div
              className={cn(
                "border-t border-slate-200/70 bg-transparent px-4 py-3 dark:border-zinc-800/90 sm:px-5",
                loading && "pointer-events-none opacity-50",
                footerClassName
              )}
            >
              {footer}
            </div>
          ) : null}
          </div>
        </div>
      </div>

    </div>
    </>,
    document.body
  );
}

interface ModalSectionProps extends HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
}

function ModalHeader({ className, children, ...props }: ModalSectionProps) {
  return (
    <div
      className={cn(
        "bg-transparent px-4 py-3 sm:px-5",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

function ModalTitle({ className, children, ...props }: ModalSectionProps) {
  return (
    <div
      className={cn(
        "text-base font-semibold leading-snug text-slate-900 dark:text-zinc-50",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

function ModalBody({ className, children, ...props }: ModalSectionProps) {
  return (
    <div className={cn("px-4 py-4 sm:px-5", className)} {...props}>
      {children}
    </div>
  );
}

function ModalFooter({ className, children, ...props }: ModalSectionProps) {
  return (
    <div
      className={cn(
        "border-t border-slate-200/70 px-4 py-3 dark:border-zinc-800/90 sm:px-5",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export namespace Modal {
  export let Header: typeof ModalHeader;
  export let Title: typeof ModalTitle;
  export let Body: typeof ModalBody;
  export let Footer: typeof ModalFooter;
}

Modal.Header = ModalHeader;
Modal.Title = ModalTitle;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;
